import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import {
  Box,
  Button,
  Container,
  Chip,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import AddIcon from '@mui/icons-material/AddOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';
import RefreshIcon from '@mui/icons-material/RefreshOutlined';

import MultiFieldTransactionSearch from 'src/components/transactions/MultiFieldTransactionSearch';
import FilterSelectInput from 'src/common/FilterSelectInput';
import TransactionListTable from 'src/components/transactions/TransactionListTable';
import TableColumnVisibilityMenu, { useTableColumnPreferences } from 'src/common/TableColumnVisibilityMenu';
import {
  invalidateTransactionListCache,
  setTransactionType
} from 'src/redux/transactionSlice';
import { getAllTanks } from 'src/redux/tankSlice';
import { getAllDispensers } from 'src/redux/dispenserSlice';
import { getAllSites } from 'src/redux/siteSlice';
import NewTransaction from 'src/components/transactions/NewTransaction';
import ExternalTransactionBulkUpload from 'src/components/transactions/externalTransactionBulkUpload';
import ReportFilterModal from 'src/components/reports/ReportFilterModal';
import DateFilterField from 'src/common/formFields/DateFilterField';
import TransactionFilters from 'src/components/transactions/TransactionFilters';
import getActiveTransactionFilters, {
  getActiveTransactionSearchFilters
} from 'src/components/transactions/transactionFilterUtils';

import {
  DATE_FORMAT,
  REPORT_FILTER_OPTIONS,
  TRANSACTION_TYPES,
  USER_ROLES
} from 'src/utils/constants';
import { transactionTableHeaders } from 'src/utils/tableHeaders';

import {
  reportFormModel,
  reportFormInitialValues,
  reportValidationSchema
} from 'src/components/reports/reportFormModel';

import {
  inwardReportFormModel,
  inwardReportFormInitialValues,
  inwardReportValidationSchema
} from 'src/components/reports/inwardReportFormModel';
import {
  externalReportFormModel,
  externalReportFormInitialValues,
  externalReportValidationSchema
} from 'src/components/reports/externalReportFormModel';

import {
  getFilterOptions,
  getReportDownloadURL,
  getReportFormTitle,
} from 'src/components/transactions/transactionService';
import useAuth from 'src/context/AuthContext';
import { setSuccessInfo } from 'src/redux/modalSlice';
import { hasAdminLikeAccess } from 'src/utils/getInitials';
import useAdminSiteFilter from 'src/hooks/useAdminSiteFilter';
import { FLEET_ONLY_TRANSACTION_TYPE, isFleetOnlyModeEnabled } from 'src/utils/appMode';
import {
  TRANSACTION_STYLE_COLORS as VEHICLE_STYLE_COLORS,
  TRANSACTION_DATE_PICKER_POPPER_SX,
  TRANSACTION_SELECT_MENU_PROPS
} from 'src/components/transactions/transactionStyleTokens';
import {
  getStoredUserTimezone,
  nowInTimezone,
  parseInStoredTimezone
} from 'src/utils/timezoneUtils';
import {
  parseTransactionQuery,
  serializeTransactionQuery
} from './transactionQuery';
import PageHeaderContext from '../context/PageHeaderContext';

const EMPTY_DATE_RANGE = { start: null, end: null };
const TRANSACTION_EXPORT_COLUMNS = {
  iot: [
    ['order_id', 'Order ID'], ['start_time', 'Delivery Time'], ['site_name', 'Site Name'],
    ['address', 'Dispenser Address'], ['nozzle_num', 'Nozzle Number'], ['vehicle_number', 'Vehicle Number'],
    ['vehicle_model', 'Vehicle Model'], ['vehicle_category', 'Vehicle Category'],
    ['odometer_entry', 'Odometer Reading'], ['opening_stock', 'Opening Stock'],
    ['closing_stock', 'Closing Stock'], ['quantity', 'Fuel Quantity']
  ],
  inward: [
    ['site_name', 'Site Name'], ['tank_name', 'Tank Name'], ['supplier', 'Supplier'],
    ['invoice_number', 'Invoice Number'], ['delivery_vehicle_number', 'Vehicle Number'],
    ['order_date', 'Order Date'], ['delivery_date', 'Delivery Date'], ['quantity', 'Fuel Quantity'],
    ['shortage_quantity', 'Shortage'], ['actual_filled', 'Actual Filled'],
    ['price_per_litre', 'Unit Price'], ['source', 'Source'], ['source_provider', 'Source Provider'],
    ['source_reference', 'Source Reference']
  ],
  transfer: [
    ['order_id', 'Order ID'], ['start_time', 'Delivery Time'], ['site_name', 'Site Name'],
    ['address', 'Dispenser Address'], ['nozzle_num', 'Nozzle Number'], ['card_num_1', 'Vehicle Card'],
    ['card_num_2', 'Operator Card'], ['vehicle_number', 'Vehicle Number'], ['vehicle_model', 'Vehicle Model'],
    ['vehicle_category', 'Vehicle Category'], ['quantity', 'Fuel Quantity']
  ],
  external: [
    ['id', 'ID'], ['site_name', 'Site Name'], ['vehicle_number', 'Vehicle Number'],
    ['date_time', 'Date Time'], ['odometer_entry', 'Odometer'], ['quantity', 'Quantity'],
    ['unit_price', 'Unit Price'], ['amount', 'Amount']
  ]
};

const TRANSACTION_PAGE_HEADERS = {
  iot: {
    title: 'TRANSACTION – ISSUED',
    subtitle: 'Manage and track issued fuel transactions.'
  },
  inward: {
    title: 'TRANSACTION – INWARD',
    subtitle: 'Manage and track inward fuel transactions.'
  },
  transfer: {
    title: 'TRANSACTION – TRANSFER',
    subtitle: 'Manage and track fuel transfer transactions.'
  },
  external: {
    title: 'TRANSACTION – EXTERNAL',
    subtitle: 'Manage and track external fuel transactions.'
  }
};

const ACTION_BUTTON_OUTLINED_SX = {
  textTransform: 'none',
  fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
  fontSize: '0.875rem',
  fontWeight: 600,
  height: 40,
  minHeight: 40,
  borderRadius: '8px',
  px: 1.75,
  whiteSpace: 'nowrap',
  borderColor: 'hsl(198 87% 32%)',
  color: 'hsl(198 87% 32%)',
  backgroundColor: '#FFFFFF',
  boxShadow: '0 1px 2px rgb(0 0 0 / 0.05)',
  '& .MuiButton-startIcon': {
    color: 'hsl(198 87% 32%)',
    mr: 0.75,
    '& svg': {
      width: 16,
      height: 16,
      fontSize: 16
    }
  },
  '&:hover': {
    borderColor: 'hsl(198 87% 32%)',
    color: 'hsl(198 87% 32%)',
    backgroundColor: 'rgba(11, 106, 153, 0.06)'
  }
};

const ACTION_BUTTON_CONTAINED_SX = {
  textTransform: 'none',
  fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
  fontSize: '0.875rem',
  fontWeight: 600,
  height: 40,
  minHeight: 40,
  borderRadius: '8px',
  px: 2,
  whiteSpace: 'nowrap',
  backgroundColor: 'hsl(198 87% 32%)',
  borderColor: 'hsl(198 87% 32%)',
  color: '#FFFFFF',
  boxShadow: '0 1px 2px rgb(15 23 42 / 0.06)',
  '& .MuiButton-startIcon': {
    color: '#FFFFFF',
    mr: 0.75,
    '& svg': {
      width: 16,
      height: 16,
      fontSize: 16
    }
  },
  '&:hover': {
    backgroundColor: 'hsl(198 87% 32%)',
    borderColor: 'hsl(198 87% 32%)',
    filter: 'brightness(0.95)'
  },
  '&:active': {
    filter: 'brightness(0.9)'
  }
};

const COLUMN_MENU_PAPER_SX = {
  color: VEHICLE_STYLE_COLORS.text,
  '& .MuiMenuItem-root': { color: VEHICLE_STYLE_COLORS.text },
  '& .MuiMenuItem-root.Mui-disabled': {
    color: `${VEHICLE_STYLE_COLORS.text} !important`,
    opacity: 1
  },
  '& .MuiListItemText-primary': { color: VEHICLE_STYLE_COLORS.text }
};

const TRANSACTION_DATE_TIME_INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: VEHICLE_STYLE_COLORS.white,
    color: VEHICLE_STYLE_COLORS.foreground,
    '& fieldset': { borderColor: VEHICLE_STYLE_COLORS.primary },
    '&:hover fieldset': { borderColor: VEHICLE_STYLE_COLORS.primary },
    '&.Mui-focused fieldset': { borderColor: VEHICLE_STYLE_COLORS.primary }
  },
  '& .MuiInputLabel-root': {
    color: VEHICLE_STYLE_COLORS.muted,
    '&.Mui-focused': { color: VEHICLE_STYLE_COLORS.primary }
  },
  '& .MuiSvgIcon-root': { color: VEHICLE_STYLE_COLORS.primary },
  '& .MuiIconButton-root': {
    color: VEHICLE_STYLE_COLORS.primary,
    '&:hover': { backgroundColor: VEHICLE_STYLE_COLORS.hover }
  },
  '& .MuiOutlinedInput-input': {
    fontSize: '0.9375rem',
    paddingRight: '0 !important'
  },
  '& .MuiInputAdornment-positionEnd': {
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'flex-end',
    gap: 0.75,
    ml: 0.75,
    mr: 0.5
  },
  '& .MuiInputAdornment-positionEnd .MuiIconButton-root': {
    flexShrink: 0
  }
};

const TransactionRefreshButton = ({ refreshing, onRefresh }) => (
  <Tooltip title="Refresh table" arrow>
    <span>
      <IconButton
        aria-label="Refresh table"
        onClick={onRefresh}
        disabled={refreshing}
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          border: `1px solid ${VEHICLE_STYLE_COLORS.primary}`,
          borderRadius: '8px',
          color: VEHICLE_STYLE_COLORS.primary,
          backgroundColor: VEHICLE_STYLE_COLORS.white,
          '&:hover': { backgroundColor: VEHICLE_STYLE_COLORS.hover },
          '&.Mui-disabled': { color: VEHICLE_STYLE_COLORS.primary, opacity: 0.7 }
        }}
      >
        {refreshing ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon fontSize="small" />}
      </IconButton>
    </span>
  </Tooltip>
);

TransactionRefreshButton.propTypes = {
  refreshing: PropTypes.bool.isRequired,
  onRefresh: PropTypes.func.isRequired
};

const TRANSFER_DATE_PICKER_SX = {
  ...TRANSACTION_DATE_TIME_INPUT_SX,
  '& .MuiInputBase-root': {
    height: 40
  },
  width: { xs: '100%', sm: 190, md: 210 },
  minWidth: 175,
  maxWidth: { xs: '100%', sm: 220, md: 230 },
  flex: { xs: '1 1 100%', sm: '1 1 185px', md: '0 1 210px' }
};

const TRANSFER_SITE_SELECT_SX = {
  width: { xs: '100%', sm: 130, md: 150 },
  minWidth: 120,
  maxWidth: { xs: '100%', sm: 160 },
  flex: { xs: '1 1 100%', sm: '1 1 120px', md: '0 1 150px' },
  mr: 0,
  '& .MuiInputBase-root': {
    height: 40
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: VEHICLE_STYLE_COLORS.white,
    color: VEHICLE_STYLE_COLORS.foreground,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: VEHICLE_STYLE_COLORS.primary },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: VEHICLE_STYLE_COLORS.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: VEHICLE_STYLE_COLORS.primary }
  },
  '& .MuiInputLabel-root': {
    color: VEHICLE_STYLE_COLORS.muted,
    '&.Mui-focused': { color: VEHICLE_STYLE_COLORS.primary }
  },
  '& .MuiSvgIcon-root': { color: VEHICLE_STYLE_COLORS.primary }
};

const Transactions = ({ location, history }) => {
  const { setPageHeader } = useContext(PageHeaderContext);
  const initialQuery = useMemo(
    () => parseTransactionQuery(location.search),
    [location.search]
  );
  const dispatch = useDispatch();
  const { getUser } = useAuth();
  const {
    isAdmin,
    siteFilter,
    setSiteFilter,
    siteOptions
  } = useAdminSiteFilter(initialQuery.site || 'all');
  const user = getUser();
  const hasAdminAccess = hasAdminLikeAccess(getUser);
  const isApprover = user?.role === USER_ROLES.approver;
  const { transactionType } = useSelector((state) => state.transactions);
  const { allTanks = [] } = useSelector((state) => state.tanks);
  const { allDispensers = [] } = useSelector((state) => state.dispensers);
  const { allSites = [] } = useSelector((state) => state.sites);
  const { appSettings } = useSelector((state) => state.settings);
  const isFleetOnlyMode = isFleetOnlyModeEnabled(appSettings);

  const [openModal, setOpenModal] = useState(false);
  const [openReportModal, setOpenReportModal] = useState(false);
  const [searchText, setSearchText] = useState(initialQuery.search);
  const [multiSearch, setMultiSearch] = useState(initialQuery.multiSearch || {});
  const [filterDateRange, setFilterDateRange] = useState({
    start: initialQuery.start || null,
    end: initialQuery.end || null
  });
  const [tankFilter, setTankFilter] = useState(initialQuery.tank || 'all');
  const [externalAppliedVehicle, setExternalAppliedVehicle] = useState(initialQuery.vehicle);
  const [externalAppliedDateRange, setExternalAppliedDateRange] = useState({
    start: initialQuery.start || null,
    end: initialQuery.end || null
  });
  const [externalAppliedSite, setExternalAppliedSite] = useState(initialQuery.site || 'all');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [draftStartDateTime, setDraftStartDateTime] = useState(null);
  const [draftEndDateTime, setDraftEndDateTime] = useState(null);
  const [draftSite, setDraftSite] = useState('all');
  const [draftTank, setDraftTank] = useState('all');
  const [filterError, setFilterError] = useState('');
  const effectiveTransactionType = isFleetOnlyMode
    ? FLEET_ONLY_TRANSACTION_TYPE
    : initialQuery.type;
  const isSales = effectiveTransactionType !== TRANSACTION_TYPES[1].id;
  const isIssued = effectiveTransactionType === TRANSACTION_TYPES[0].id;
  const isInward = effectiveTransactionType === TRANSACTION_TYPES[1].id;
  const isExternal = effectiveTransactionType === TRANSACTION_TYPES[3].id;
  const isTransferCategory = effectiveTransactionType === TRANSACTION_TYPES[2].id;
  const activeTransactionHeaders = useMemo(
    () => transactionTableHeaders[effectiveTransactionType] || [],
    [effectiveTransactionType]
  );
  const transactionColumnPreferences = useTableColumnPreferences(
    `transactions_${effectiveTransactionType}`,
    activeTransactionHeaders
  );
  const canAddTransaction = isInward ? hasAdminAccess || isApprover : hasAdminAccess;
  const [openBulkUploadModal, setOpenBulkUploadModal] = useState(false);
  const [transactionRefreshKey, setTransactionRefreshKey] = useState(0);
  const [manualRefreshRequestId, setManualRefreshRequestId] = useState(0);
  const [isTableRefreshing, setIsTableRefreshing] = useState(false);
  const canUploadExternalTransactions = isExternal
    && [
      USER_ROLES.admin,
      USER_ROLES.siteManager,
      USER_ROLES.requestor,
      USER_ROLES.approver
    ].includes(user?.role)
    && Boolean(appSettings?.external_transaction_upload_enabled);
  const appliedDateFilter = isExternal ? externalAppliedDateRange : filterDateRange;
  const appliedSiteFilter = isExternal ? externalAppliedSite : siteFilter;
  const listSearchText = isExternal ? initialQuery.vehicle : initialQuery.search;
  const listMultiSearch = initialQuery.multiSearch || {};
  const listDateFilter = useMemo(() => ({
    start: initialQuery.start || null,
    end: initialQuery.end || null
  }), [initialQuery.end, initialQuery.start]);
  const listSiteFilter = initialQuery.site || 'all';
  const listTankFilter = initialQuery.tank || 'all';

  useEffect(() => {
    setPageHeader(TRANSACTION_PAGE_HEADERS[effectiveTransactionType]);
    return () => setPageHeader(null);
  }, [effectiveTransactionType, setPageHeader]);
  const reportDownloadUrl = getReportDownloadURL(effectiveTransactionType);
  const filterOptions = getFilterOptions(effectiveTransactionType);
  const activeExportColumns = useMemo(
    () => (TRANSACTION_EXPORT_COLUMNS[effectiveTransactionType] || []).map(([id, label]) => ({ id, label })),
    [effectiveTransactionType]
  );
  const externalReportFilterOptions = {
    ...REPORT_FILTER_OPTIONS,
    dispenser: false,
    vehicle: false,
    vehicleGroup: false,
    assetType: false
  };
  let activeReportFormModel = inwardReportFormModel;
  let activeReportValidation = inwardReportValidationSchema;
  let activeReportInitialValues = inwardReportFormInitialValues;
  if (isExternal) {
    activeReportFormModel = externalReportFormModel;
    activeReportValidation = externalReportValidationSchema;
    activeReportInitialValues = externalReportFormInitialValues;
  } else if (isSales) {
    activeReportFormModel = reportFormModel;
    activeReportValidation = reportValidationSchema;
    activeReportInitialValues = reportFormInitialValues;
  }

  const replaceTransactionQuery = (query) => {
    const queryString = serializeTransactionQuery(query);
    history.replace({
      pathname: location.pathname,
      search: queryString ? `?${queryString}` : ''
    });
  };

  useEffect(() => {
    const query = parseTransactionQuery(location.search);
    const restoredType = isFleetOnlyMode ? FLEET_ONLY_TRANSACTION_TYPE : query.type;
    if (transactionType !== restoredType) dispatch(setTransactionType(restoredType));

    const nextStart = query.start || null;
    const nextEnd = query.end || null;
    const nextSite = query.site || 'all';
    const nextTank = query.tank || 'all';

    setSearchText((current) => (current === query.search ? current : query.search));
    setMultiSearch((current) => (
      JSON.stringify(current) === JSON.stringify(query.multiSearch || {})
        ? current
        : (query.multiSearch || {})
    ));
    setFilterDateRange((current) => (
      current.start === nextStart && current.end === nextEnd
        ? current
        : { start: nextStart, end: nextEnd }
    ));
    setTankFilter((current) => (current === nextTank ? current : nextTank));
    setExternalAppliedVehicle((current) => (
      current === query.vehicle ? current : query.vehicle
    ));
    setExternalAppliedDateRange((current) => (
      current.start === nextStart && current.end === nextEnd
        ? current
        : { start: nextStart, end: nextEnd }
    ));
    setExternalAppliedSite((current) => (current === nextSite ? current : nextSite));
    setSiteFilter((current) => (current === nextSite ? current : nextSite));
  }, [dispatch, isFleetOnlyMode, location.search, setSiteFilter, transactionType]);

  const tankOptions = useMemo(() => {
    const filteredTanks = siteFilter === 'all'
      ? allTanks
      : allTanks.filter((tank) => String(tank.site) === String(siteFilter));
    return [
      { id: 'all', name: 'All Tanks' },
      ...filteredTanks.map((tank) => ({
        ...tank,
        name: tank.name || tank.tank_name || `Tank ${tank.id}`
      }))
    ];
  }, [allTanks, siteFilter]);

  const draftTankOptions = useMemo(() => {
    const filteredTanks = draftSite === 'all'
      ? allTanks
      : allTanks.filter((tank) => String(tank.site) === String(draftSite));
    return [
      { id: 'all', name: 'All Tanks' },
      ...filteredTanks.map((tank) => ({
        ...tank,
        name: tank.name || tank.tank_name || `Tank ${tank.id}`
      }))
    ];
  }, [allTanks, draftSite]);

  const activeFilters = getActiveTransactionFilters({
    isExternal,
    isIssued,
    isInward,
    site: appliedSiteFilter,
    tank: tankFilter,
    start: appliedDateFilter.start,
    end: appliedDateFilter.end
  });
  const activeSearchFilters = getActiveTransactionSearchFilters({
    multiSearch: listMultiSearch,
    legacySearch: isExternal ? initialQuery.vehicle : initialQuery.search
  });

  const getAppliedTransactionQuery = () => ({
    type: effectiveTransactionType,
    search: searchText,
    vehicle: externalAppliedVehicle,
    site: isExternal ? externalAppliedSite : siteFilter,
    tank: tankFilter,
    start: isExternal ? externalAppliedDateRange.start : filterDateRange.start,
    end: isExternal ? externalAppliedDateRange.end : filterDateRange.end,
    multiSearch
  });

  const handleMultiSearch = (values) => {
    setMultiSearch(values);
    replaceTransactionQuery({
      ...getAppliedTransactionQuery(),
      multiSearch: values
    });
  };

  const clearMultiSearch = () => {
    setMultiSearch({});
    replaceTransactionQuery({ ...getAppliedTransactionQuery(), multiSearch: {} });
  };

  const handleSiteChange = (value) => {
    setSiteFilter(value);
    replaceTransactionQuery({
      ...getAppliedTransactionQuery(),
      site: value,
    });
  };

  useEffect(() => {
    if (isFleetOnlyMode && transactionType !== FLEET_ONLY_TRANSACTION_TYPE) {
      dispatch(setTransactionType(FLEET_ONLY_TRANSACTION_TYPE));
    }
  }, [dispatch, isFleetOnlyMode, transactionType]);

  useEffect(() => {
    if (isAdmin && (isIssued || isInward) && allTanks.length === 0) {
      dispatch(getAllTanks());
    }
  }, [allTanks.length, dispatch, isAdmin, isInward, isIssued]);

  useEffect(() => {
    if (openBulkUploadModal && allDispensers.length === 0) dispatch(getAllDispensers());
    if (openBulkUploadModal && allSites.length === 0) dispatch(getAllSites());
  }, [allDispensers.length, allSites.length, dispatch, openBulkUploadModal]);

  const handleExternalUploadComplete = (result) => {
    dispatch(invalidateTransactionListCache());
    setTransactionRefreshKey((current) => current + 1);
    dispatch(setSuccessInfo({
      successMsg: `${result?.created_count || 0} external transactions created successfully.`
    }));
  };

  const handleRefreshTable = () => {
    if (isTableRefreshing) return;
    setIsTableRefreshing(true);
    setManualRefreshRequestId((current) => current + 1);
  };

  useEffect(() => {
    if (!isIssued && !isInward) {
      setTankFilter('all');
      return;
    }
    if (allTanks.length === 0) return;

    const hasSelectedTank = tankOptions.some(
      (tank) => String(tank.id) === String(tankFilter)
    );
    if (!hasSelectedTank) setTankFilter('all');
  }, [tankFilter, tankOptions, isIssued]);

  const getDateTimeFilterString = (newValue) => (
    newValue && newValue.isValid()
      ? moment.tz(
        newValue.format(DATE_FORMAT.unixDateTime),
        DATE_FORMAT.unixDateTime,
        true,
        getStoredUserTimezone()
      ).format(DATE_FORMAT.unixDateTime)
      : null
  );

  const setDateRangeField = (fieldName, newValue) => {
    const nextDateRange = {
      ...filterDateRange,
      [fieldName]: getDateTimeFilterString(newValue)
    };
    setFilterDateRange(() => nextDateRange);
    replaceTransactionQuery({
      ...getAppliedTransactionQuery(),
      start: nextDateRange.start,
      end: nextDateRange.end
    });
  };

  const clearDateRangeFilter = () => {
    setFilterDateRange(EMPTY_DATE_RANGE);
    replaceTransactionQuery({
      ...getAppliedTransactionQuery(),
      start: null,
      end: null
    });
  };
  const openFilterPopover = (e) => {
    const appliedDateRange = isExternal ? externalAppliedDateRange : filterDateRange;
    const now = nowInTimezone(getStoredUserTimezone()).seconds(0).milliseconds(0);
    setDraftStartDateTime(
      appliedDateRange.start
        ? parseInStoredTimezone(appliedDateRange.start, DATE_FORMAT.unixDateTime)
        : now.clone().startOf('day')
    );
    setDraftEndDateTime(
      appliedDateRange.end
        ? parseInStoredTimezone(appliedDateRange.end, DATE_FORMAT.unixDateTime)
        : now
    );
    setDraftSite(isExternal ? externalAppliedSite || 'all' : siteFilter || 'all');
    setDraftTank(isExternal ? 'all' : tankFilter || 'all');
    setFilterError('');
    setFilterAnchorEl(e.currentTarget);
  };

  const closeFilterPopover = () => {
    setFilterAnchorEl(null);
  };

  const handleApplyFilters = () => {
    if (!draftStartDateTime || !draftEndDateTime) {
      setFilterError('Start date time and end date time are required.');
      return;
    }
    if (draftEndDateTime.isBefore(draftStartDateTime)) {
      setFilterError('End date time must be after start date time.');
      return;
    }
    const appliedDateRange = {
      start: draftStartDateTime.format(DATE_FORMAT.unixDateTime),
      end: draftEndDateTime.format(DATE_FORMAT.unixDateTime)
    };
    if (isExternal) {
      setExternalAppliedDateRange(appliedDateRange);
      setExternalAppliedSite(draftSite || 'all');
      replaceTransactionQuery({
        ...getAppliedTransactionQuery(),
        site: draftSite || 'all',
        start: appliedDateRange.start,
        end: appliedDateRange.end
      });
    } else {
      setFilterDateRange(appliedDateRange);
      setSiteFilter(draftSite || 'all');
      setTankFilter(draftTank || 'all');
      replaceTransactionQuery({
        ...getAppliedTransactionQuery(),
        site: draftSite || 'all',
        tank: draftTank || 'all',
        start: appliedDateRange.start,
        end: appliedDateRange.end
      });
    }
    closeFilterPopover();
  };

  const handleClearDraftFilters = () => {
    setDraftStartDateTime(null);
    setDraftEndDateTime(null);
    setDraftSite('all');
    setDraftTank('all');
    setFilterError('');

    if (isExternal) {
      setExternalAppliedDateRange(EMPTY_DATE_RANGE);
      setExternalAppliedSite('all');
      replaceTransactionQuery({
        ...getAppliedTransactionQuery(),
        site: 'all',
        start: null,
        end: null
      });
    } else {
      setFilterDateRange(EMPTY_DATE_RANGE);
      setSiteFilter('all');
      setTankFilter('all');
      replaceTransactionQuery({
        ...getAppliedTransactionQuery(),
        site: 'all',
        tank: 'all',
        start: null,
        end: null
      });
    }

    closeFilterPopover();
  };

  const clearSearchFilter = (filter) => {
    if (filter.key === 'legacy_search') {
      if (isExternal) setExternalAppliedVehicle('');
      else setSearchText('');
      replaceTransactionQuery({
        ...getAppliedTransactionQuery(),
        search: isExternal ? searchText : '',
        vehicle: isExternal ? '' : externalAppliedVehicle
      });
      return;
    }
    const nextSearch = { ...listMultiSearch };
    delete nextSearch[filter.key];
    setMultiSearch(nextSearch);
    replaceTransactionQuery({ ...getAppliedTransactionQuery(), multiSearch: nextSearch });
  };

  const clearAllSearchFilters = () => {
    setMultiSearch({});
    setSearchText('');
    setExternalAppliedVehicle('');
    replaceTransactionQuery({
      ...getAppliedTransactionQuery(),
      search: '',
      vehicle: '',
      multiSearch: {}
    });
  };

  const renderFilterGroup = (title, filters, onDelete, onClear, variant) => {
    if (!filters.length) return null;
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        useFlexGap
        sx={{
          width: '100%',
          minWidth: 0,
          flexWrap: 'wrap',
          columnGap: 1,
          rowGap: 0.5,
          flexShrink: 0,
          '& .MuiTypography-root': { color: VEHICLE_STYLE_COLORS.text }
        }}
      >
        <Typography
          variant="body2"
          sx={{
            flex: '0 0 auto',
            lineHeight: '28px',
            fontWeight: 600
          }}
        >
          {title}
          :
        </Typography>
        {filters.map((filter) => {
          let optionList = [];
          if (variant === 'active') optionList = filter.key === 'site' ? siteOptions : tankOptions;
          const option = optionList.find((item) => String(item.id) === String(filter.value));
          let value = option?.name || filter.value;
          if (filter.key === 'date') {
            value = [appliedDateFilter.start, appliedDateFilter.end]
              .filter(Boolean)
              .map((date) => parseInStoredTimezone(date, DATE_FORMAT.unixDateTime).format('DD MMM YYYY'))
              .filter((date, index, dates) => index === 0 || date !== dates[0])
              .join(' – ');
          }
          return (
            <Chip
              key={filter.key}
              size="small"
              variant="outlined"
              label={`${filter.label}: ${value}`}
              onDelete={() => onDelete(filter)}
              sx={{
                maxWidth: { xs: '100%', sm: 360 },
                color: VEHICLE_STYLE_COLORS.text,
                backgroundColor: variant === 'search' ? VEHICLE_STYLE_COLORS.selected : '#f8fafc',
                borderColor: variant === 'search'
                  ? VEHICLE_STYLE_COLORS.primary
                  : VEHICLE_STYLE_COLORS.border,
                '& .MuiChip-deleteIcon': { color: VEHICLE_STYLE_COLORS.muted },
                '& .MuiChip-deleteIcon:hover': { color: VEHICLE_STYLE_COLORS.text },
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                },
                '&:hover': { backgroundColor: VEHICLE_STYLE_COLORS.hover }
              }}
            />
          );
        })}
        <Button
          size="small"
          onClick={onClear}
          sx={{
            textTransform: 'none',
            minWidth: 0,
            flexShrink: 0,
            fontWeight: 600,
            color: VEHICLE_STYLE_COLORS.primary,
            '&:hover': { backgroundColor: VEHICLE_STYLE_COLORS.hover }
          }}
        >
          {variant === 'search' ? 'Clear search' : 'Clear filters'}
        </Button>
      </Stack>
    );
  };

  const renderIssuedToolbar = () => (
    <Box
      sx={{
        mt: 2,
        mb: 1.5,
        width: '100%',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        justifyContent: 'space-between',
        gap: 1.5,
        flexWrap: 'wrap'
      }}
    >
      <Box
        sx={{
          flex: { xs: '1 1 100%', md: '0 1 360px' },
          maxWidth: { xs: '100%', md: 360, lg: 360 },
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75
        }}
      >
        <MultiFieldTransactionSearch
          transactionType={effectiveTransactionType}
          appliedValues={multiSearch}
          onApply={handleMultiSearch}
          onClear={clearMultiSearch}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <TransactionRefreshButton refreshing={isTableRefreshing} onRefresh={handleRefreshTable} />
      </Box>

      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{
          justifyContent: { xs: 'flex-start', sm: 'flex-end' },
          flexShrink: 0,
          maxWidth: '100%',
          minWidth: 0
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<FilterAltOutlinedIcon />}
          onClick={openFilterPopover}
          sx={ACTION_BUTTON_OUTLINED_SX}
        >
          Filters
        </Button>
        <TableColumnVisibilityMenu
          columns={activeTransactionHeaders}
          hiddenColumnIds={transactionColumnPreferences.hiddenColumnIds}
          onToggleColumn={transactionColumnPreferences.onToggleColumn}
          buttonLabel="Columns"
          buttonSx={ACTION_BUTTON_OUTLINED_SX}
          menuPaperSx={COLUMN_MENU_PAPER_SX}
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => setOpenReportModal(true)}
          sx={ACTION_BUTTON_CONTAINED_SX}
        >
          Export
        </Button>
      </Stack>
    </Box>
  );

  const renderInwardToolbar = () => (
    <Box
      sx={{
        mt: 2,
        mb: 1.5,
        width: '100%',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        justifyContent: 'space-between',
        gap: 1.5,
        flexWrap: 'wrap'
      }}
    >
      <Box
        sx={{
          flex: { xs: '1 1 100%', md: '0 1 360px' },
          maxWidth: { xs: '100%', md: 360, lg: 360 },
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75
        }}
      >
        <MultiFieldTransactionSearch
          transactionType={effectiveTransactionType}
          appliedValues={multiSearch}
          onApply={handleMultiSearch}
          onClear={clearMultiSearch}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <TransactionRefreshButton refreshing={isTableRefreshing} onRefresh={handleRefreshTable} />
      </Box>

      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{
          justifyContent: { xs: 'flex-start', sm: 'flex-end' },
          flexShrink: 0,
          maxWidth: '100%',
          minWidth: 0
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<FilterAltOutlinedIcon />}
          onClick={openFilterPopover}
          sx={ACTION_BUTTON_OUTLINED_SX}
        >
          Filters
        </Button>
        <TableColumnVisibilityMenu
          columns={activeTransactionHeaders}
          hiddenColumnIds={transactionColumnPreferences.hiddenColumnIds}
          onToggleColumn={transactionColumnPreferences.onToggleColumn}
          buttonLabel="Columns"
          buttonSx={ACTION_BUTTON_OUTLINED_SX}
          menuPaperSx={COLUMN_MENU_PAPER_SX}
        />
        {canAddTransaction && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setOpenModal(true)}
            sx={ACTION_BUTTON_CONTAINED_SX}
          >
            Add Transaction
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => setOpenReportModal(true)}
          sx={ACTION_BUTTON_CONTAINED_SX}
        >
          Export
        </Button>
      </Stack>
    </Box>
  );

  const renderExternalToolbar = () => (
    <Box
      sx={{
        mt: 2,
        mb: 1.5,
        width: '100%',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        justifyContent: 'space-between',
        gap: 1.5,
        flexWrap: 'wrap'
      }}
    >
      <Box
        sx={{
          flex: { xs: '1 1 100%', md: '0 1 360px' },
          maxWidth: { xs: '100%', md: 360, lg: 360 },
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75
        }}
      >
        <MultiFieldTransactionSearch
          transactionType={effectiveTransactionType}
          appliedValues={multiSearch}
          onApply={handleMultiSearch}
          onClear={clearMultiSearch}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <TransactionRefreshButton refreshing={isTableRefreshing} onRefresh={handleRefreshTable} />
      </Box>

      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{
          justifyContent: { xs: 'flex-start', sm: 'flex-end' },
          flexShrink: 0,
          maxWidth: '100%',
          minWidth: 0
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<FilterAltOutlinedIcon />}
          onClick={openFilterPopover}
          sx={ACTION_BUTTON_OUTLINED_SX}
        >
          Filters
        </Button>
        <TableColumnVisibilityMenu
          columns={activeTransactionHeaders}
          hiddenColumnIds={transactionColumnPreferences.hiddenColumnIds}
          onToggleColumn={transactionColumnPreferences.onToggleColumn}
          buttonLabel="Columns"
          buttonSx={ACTION_BUTTON_OUTLINED_SX}
          menuPaperSx={COLUMN_MENU_PAPER_SX}
        />
        {canUploadExternalTransactions && (
          <Button
            variant="contained"
            size="small"
            startIcon={<UploadFileIcon />}
            onClick={() => setOpenBulkUploadModal(true)}
            sx={ACTION_BUTTON_CONTAINED_SX}
          >
            Upload Excel
          </Button>
        )}
        {canAddTransaction && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setOpenModal(true)}
            sx={ACTION_BUTTON_CONTAINED_SX}
          >
            Add Transaction
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => setOpenReportModal(true)}
          sx={ACTION_BUTTON_CONTAINED_SX}
        >
          Export
        </Button>
      </Stack>
    </Box>
  );

  const renderTransferToolbar = () => (
    <Box
      sx={{
        mt: 2,
        mb: 1.5,
        width: '100%',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        justifyContent: 'space-between',
        gap: 1.5,
        flexWrap: { xs: 'wrap', lg: 'nowrap' }
      }}
    >
      <Box
        sx={{
          flex: { xs: '1 1 100%', md: '1 1 0', lg: '1 1 0' },
          maxWidth: { xs: '100%', md: 'none', lg: 'none' },
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75
        }}
      >
        <MultiFieldTransactionSearch
          transactionType={effectiveTransactionType}
          appliedValues={multiSearch}
          onApply={handleMultiSearch}
          onClear={clearMultiSearch}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <TransactionRefreshButton refreshing={isTableRefreshing} onRefresh={handleRefreshTable} />
      </Box>

      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        flexWrap={{ xs: 'wrap', lg: 'nowrap' }}
        useFlexGap
        sx={{
          flex: '0 0 auto',
          justifyContent: { xs: 'flex-start', md: 'flex-end' },
          minWidth: 0,
          flexShrink: 0,
          maxWidth: '100%'
        }}
      >
        <DateFilterField
          label="Start Date Time"
          pickerPopperSx={TRANSACTION_DATE_PICKER_POPPER_SX}
          shrinkLabel
          value={filterDateRange.start
            ? parseInStoredTimezone(filterDateRange.start, DATE_FORMAT.unixDateTime)
            : null}
          onlyDate={false}
          onDateChange={(newValue) => setDateRangeField('start', newValue)}
          onClearFilter={clearDateRangeFilter}
          pickerProps={{
            maxDateTime: filterDateRange.end
              ? parseInStoredTimezone(filterDateRange.end, DATE_FORMAT.unixDateTime)
              : undefined
          }}
          sx={TRANSFER_DATE_PICKER_SX}
        />
        <DateFilterField
          label="End Date Time"
          pickerPopperSx={TRANSACTION_DATE_PICKER_POPPER_SX}
          shrinkLabel
          value={filterDateRange.end
            ? parseInStoredTimezone(filterDateRange.end, DATE_FORMAT.unixDateTime)
            : null}
          onlyDate={false}
          onDateChange={(newValue) => setDateRangeField('end', newValue)}
          onClearFilter={clearDateRangeFilter}
          pickerProps={{
            minDateTime: filterDateRange.start
              ? parseInStoredTimezone(filterDateRange.start, DATE_FORMAT.unixDateTime)
              : undefined
          }}
          sx={TRANSFER_DATE_PICKER_SX}
        />
        {isAdmin && (
          <FilterSelectInput
            label="Site"
            name="site"
            value={siteFilter}
            data={siteOptions}
            onInputChange={handleSiteChange}
            menuProps={TRANSACTION_SELECT_MENU_PROPS}
            sx={TRANSFER_SITE_SELECT_SX}
          />
        )}
        <TableColumnVisibilityMenu
          columns={activeTransactionHeaders}
          hiddenColumnIds={transactionColumnPreferences.hiddenColumnIds}
          onToggleColumn={transactionColumnPreferences.onToggleColumn}
          buttonLabel="Columns"
          buttonSx={ACTION_BUTTON_OUTLINED_SX}
          menuPaperSx={COLUMN_MENU_PAPER_SX}
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => setOpenReportModal(true)}
          sx={ACTION_BUTTON_CONTAINED_SX}
        >
          Export
        </Button>
      </Stack>
    </Box>
  );

  return (
    <>
      <Helmet>
        <title>Transactions | Retrack FMS</title>
      </Helmet>
      <Box
        sx={{
          background: 'radial-gradient(circle at 92% 4%, rgba(11, 110, 153, 0.12), transparent 28rem), linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          minHeight: 'calc(100vh - 64px)',
          height: 'auto',
          overflow: 'visible',
          pt: 0,
          pb: 0
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: 'auto',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >

          {isIssued && renderIssuedToolbar()}
          {isInward && renderInwardToolbar()}
          {isTransferCategory && renderTransferToolbar()}
          {isExternal && renderExternalToolbar()}

          <TransactionFilters
            anchorEl={filterAnchorEl}
            onClose={closeFilterPopover}
            onApply={handleApplyFilters}
            onClear={handleClearDraftFilters}
            isIssued={isIssued}
            isInward={isInward}
            siteOptions={siteOptions}
            tankOptions={draftTankOptions}
            draftStartDateTime={draftStartDateTime}
            setDraftStartDateTime={(value) => {
              setDraftStartDateTime(value);
              setFilterError('');
            }}
            draftEndDateTime={draftEndDateTime}
            setDraftEndDateTime={(value) => {
              setDraftEndDateTime(value);
              setFilterError('');
            }}
            draftSite={draftSite}
            setDraftSite={setDraftSite}
            draftTank={draftTank}
            setDraftTank={setDraftTank}
            filterError={filterError}
          />

          {renderFilterGroup('Search Filters', activeSearchFilters, clearSearchFilter, clearAllSearchFilters, 'search')}
          {renderFilterGroup('Active Filters', activeFilters, (filter) => {
            const nextSite = filter.key === 'site' ? 'all' : appliedSiteFilter;
            const nextTank = filter.key === 'tank' ? 'all' : tankFilter;
            const nextDate = filter.key === 'date' ? EMPTY_DATE_RANGE : appliedDateFilter;
            if (filter.key === 'site') {
              if (isExternal) setExternalAppliedSite('all');
              else setSiteFilter('all');
            } else if (filter.key === 'tank') {
              setTankFilter('all');
            } else if (filter.key === 'date') {
              if (isExternal) setExternalAppliedDateRange(EMPTY_DATE_RANGE);
              else setFilterDateRange(EMPTY_DATE_RANGE);
            }
            replaceTransactionQuery({
              ...getAppliedTransactionQuery(),
              site: nextSite,
              tank: nextTank,
              start: nextDate.start,
              end: nextDate.end
            });
          }, handleClearDraftFilters, 'active')}

          {(activeSearchFilters.length > 0 || activeFilters.length > 0) && (
            <Box aria-hidden="true" sx={{ height: 4, flex: '0 0 4px' }} />
          )}

          {/* TOOLBAR (Everything inside same box) */}

          {/* TABLE */}
          <Box
            sx={{
              mt: 0,
              flex: '0 0 auto',
              minHeight: 'auto',
              width: '100%',
              overflow: 'visible'
            }}
          >
            <TransactionListTable
              refreshData={transactionRefreshKey || location.state?.fromDashboard}
              refreshRequestId={manualRefreshRequestId}
              onRefreshStateChange={setIsTableRefreshing}
              searchText={listSearchText}
              multiSearch={listMultiSearch}
              dateFilter={listDateFilter}
              siteFilter={listSiteFilter}
              tankFilter={(isIssued || isInward) && isAdmin ? listTankFilter : 'all'}
              isSales={isSales}
              transactionTypeOverride={effectiveTransactionType}
              columnPreferences={transactionColumnPreferences}
            />
          </Box>

          {/* MODALS */}
          <NewTransaction
            open={openModal}
            transactionType={effectiveTransactionType}
            handleClose={() => setOpenModal(false)}
          />

          <ExternalTransactionBulkUpload
            open={openBulkUploadModal}
            onClose={() => setOpenBulkUploadModal(false)}
            dispensers={allDispensers}
            sites={allSites}
            onComplete={handleExternalUploadComplete}
          />

          <ReportFilterModal
            open={openReportModal}
            title={getReportFormTitle(effectiveTransactionType)}
            formModel={activeReportFormModel}
            formValidation={activeReportValidation}
            formInitValues={activeReportInitialValues}
            filterConfig={isExternal ? externalReportFilterOptions : filterOptions}
            downloadUrl={reportDownloadUrl}
            exportColumns={activeExportColumns}
            handleClose={() => setOpenReportModal(false)}
          />

        </Container>
      </Box>
    </>
  );
};

Transactions.propTypes = {
  location: PropTypes.object,
  history: PropTypes.object
};

export default Transactions;
